import { useState, useEffect, useMemo } from "react";
import {
  Card,
  Typography,
  Spin,
  Alert,
  Empty,
  Divider,
} from "antd";
import {
  ShopOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { getHospitalityAnalyticsFromServer } from "./hospitalityAnalyticsApi";
import {
  HospitalityAnalytics as HospitalityAnalyticsType,
} from "./interfaces/hospitalityAnalytics.interface";
import styles from "./HospitalityAnalytics.module.css";
import HospitalityFilters from "./components/HospitalityFilters";
import HospitalitySummaryCards from "./components/HospitalitySummaryCards";
import HospitalitySegmentStats from "./components/HospitalitySegmentStats";
import HospitalityCustomerTable from "./components/HospitalityCustomerTable";

const { Title, Text } = Typography;

export default function HospitalityAnalytics() {
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [data, setData] = useState<HospitalityAnalyticsType | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Data collection started on Dec 5, 2025
  const DATA_COLLECTION_START_DATE = dayjs("2025-12-05");
  const today = dayjs();

  const [selectedBusinessId, setSelectedBusinessId] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
    DATA_COLLECTION_START_DATE,
    today,
  ]);
  const [segment, setSegment] = useState<string>("all");
  const [includeCustomerList, setIncludeCustomerList] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(100);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const fetchData = async () => {
    if (!selectedBusinessId) {
      setData(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const params: any = {
        businessId: selectedBusinessId,
        includeCustomerList: includeCustomerList,
        limit: limit || 100,
      };

      if (dateRange[0] && dateRange[1]) {
        params.startDate = dateRange[0].format("YYYY-MM-DD");
        params.endDate = dateRange[1].format("YYYY-MM-DD");
      }

      if (segment && segment !== "all") {
        params.segment = segment;
      }

      const result = await getHospitalityAnalyticsFromServer(params);
      if (result) {
        setData(result);
      } else {
        setError("No data returned from server");
        setData(null);
      }
    } catch (err: any) {
      console.error("Error fetching hospitality analytics:", err);
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load analytics data. Please try again.";
      setError(errorMessage);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch when business is selected or filters change
  useEffect(() => {
    if (selectedBusinessId) {
      fetchData();
    } else {
      setData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBusinessId, dateRange, segment, includeCustomerList, limit]);

  const handleRefresh = () => {
    fetchData();
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Title level={2}>
          <ShopOutlined /> Hospitality Analytics
        </Title>
        <Text type="secondary">
          Discover customer insights for your hospitality business. Analyze visit
          patterns, customer segments, and lifetime value to optimize your operations.
        </Text>
      </div>

      <HospitalityFilters
        selectedBusinessId={selectedBusinessId}
        onBusinessChange={setSelectedBusinessId}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        segment={segment}
        onSegmentChange={setSegment}
        includeCustomerList={includeCustomerList}
        onIncludeCustomerListChange={setIncludeCustomerList}
        limit={limit}
        onLimitChange={setLimit}
        onRefresh={handleRefresh}
        loading={loading}
        isMobile={isMobile}
      />

      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          className={styles.error_container}
          style={{ marginBottom: 24 }}
        />
      )}

      {loading && !data ? (
        <div className={styles.loading_container}>
          <Spin size="large" tip="Loading analytics data..." />
        </div>
      ) : !selectedBusinessId ? (
        <div className={styles.empty_container}>
          <Empty
            description={
              <Text type="secondary">
                Please select a hospitality merchant to view analytics
              </Text>
            }
          />
        </div>
      ) : data ? (
        <>
          <div className={styles.summary_section}>
            <Title level={4}>Summary Statistics</Title>
            <HospitalitySummaryCards summary={data.summary} isMobile={isMobile} />
          </div>

          <Divider />

          <div className={styles.segment_section}>
            <Title level={4}>Customer Segments</Title>
            <HospitalitySegmentStats segments={data.segments} isMobile={isMobile} />
          </div>

          {includeCustomerList && data.customers && data.customers.length > 0 && (
            <>
              <Divider />
              <HospitalityCustomerTable
                customers={data.customers}
                loading={loading}
                isMobile={isMobile}
              />
            </>
          )}

          {includeCustomerList && (!data.customers || data.customers.length === 0) && (
            <div className={styles.empty_container}>
              <Empty
                description={
                  <Text type="secondary">No customer data available for the selected filters</Text>
                }
              />
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

